/**
 * 四角偵測 API 送出圖片前的等比縮放，與拿到角點後換算回全解析度來源座標系的
 * 共用純函式（kanban 114186 code review）。
 *
 * 背景：UploadEssay.tsx 的 correctionSourceToApiImage() 把來源圖壓縮到長邊
 * maxLongSide 再送去 API，但 perspectivePoints 存的是全解析度來源 canvas 的座標系。
 * 兩者要用同一個縮放比例換算，寫錯的症狀是「角點無聲跑位」——選圖當下已經先用
 * compressImageForUpload 壓過一次，長邊剛好也是 1280，兩次縮放比例恆等於 1，
 * 不換算也看不出錯；只要上游任一邊調整了長邊上限，角點就會無聲錯位。
 *
 * 只留算得動、測得到的純數學部分：canvas 繪圖依賴 DOM，這裡不放
 * （專案的 vitest 是 node 環境、沒有 jsdom，見 rotationUi.ts 同樣的取捨）。
 */

/**
 * 計算送 API 前的等比縮放比例：只縮不放，長邊超過 maxLongSide 才縮。
 * 回傳值 <= 1；resizedLongSide = max(width, height) * scale。
 */
export function computeDownscaleFactor(
  width: number,
  height: number,
  maxLongSide: number
): number {
  const longSide = Math.max(width, height);
  return longSide > maxLongSide ? maxLongSide / longSide : 1;
}

/**
 * 把 API 回傳的角點（縮圖座標系）換算回來源圖（全解析度）座標系。
 * scale 是 computeDownscaleFactor 算出的送出縮放比例，換算方向是除法：
 * 縮圖座標 / scale = 全解析度座標（scale <= 1，所以是放大回去）。
 */
export function scalePointToSource(
  point: readonly [number, number],
  scale: number
): { x: number; y: number } {
  return { x: point[0] / scale, y: point[1] / scale };
}
